// Component: operandLinking.js
// Purpose: The one pure writer for Strategy Builder operand links, fields, constants, and edge-only associations.

import { LOGIC_NODE_TYPES } from '../config/nodeVisualConfig';
import { defaultLogicInputs } from './nodeUtils';

const OPERAND_SOURCE_TYPES = new Set([
  'data_source', 'indicator', 'math', 'constant', ...LOGIC_NODE_TYPES, 'signal', 'position_trigger',
]);
const BOOLEAN_TARGET_TYPES = new Set(['logic_gate', 'signal', 'position_trigger']);
const BOOLEAN_TRIGGER_ACTIONS = new Set(['sl', 'tp', 'time_exit']);
const NUMERIC_OPERAND_TARGET_TYPES = new Set(['comparator', 'math']);
const NUMERIC_OPERAND_SOURCE_TYPES = new Set([
  'data_source', 'indicator', 'math', 'comparator', 'time_window', 'constant',
]);
const INPUT_SLOT_TARGET_TYPES = new Set(['comparator', 'logic_gate', 'math', 'signal', 'position_trigger']);

// One input-affordance authority shared by graph wiring and the Tree projection. Callers render
// a condition catalogue for boolean targets, a value picker for numeric targets, and nothing for leaves.
export const targetInputKind = (targetNode) => {
  if (BOOLEAN_TARGET_TYPES.has(targetNode?.type)) return 'boolean';
  if (NUMERIC_OPERAND_TARGET_TYPES.has(targetNode?.type)) return 'operand';
  return null;
};

const isExpandableLogicTarget = (targetNode) => (
  targetNode?.type === 'logic_gate' && String(targetNode.data?.operator || 'and').toLowerCase() !== 'not'
);

export const nextInputSlotId = (inputs = []) => {
  const indexed = inputs
    .map((input) => /^(input_)?(\d+)$/.exec(String(input?.id || '')))
    .filter(Boolean);
  const nextIndex = indexed.reduce((max, match) => Math.max(max, Number(match[2]) + 1), inputs.length);
  const prefix = indexed.find((match) => match[1])?.[1]
    || (inputs.some((input) => String(input?.id || '').startsWith('input_')) ? 'input_' : '');
  return prefix + nextIndex;
};

export const eligibleOperandSources = (nodes = [], targetNode) => {
  const inputKind = targetInputKind(targetNode);
  return (nodes || []).filter((candidate) => {
    if (!candidate || candidate.id === targetNode?.id || !OPERAND_SOURCE_TYPES.has(candidate.type)) return false;
    if (inputKind === 'operand') {
      return NUMERIC_OPERAND_SOURCE_TYPES.has(candidate.type);
    }
    if (inputKind !== 'boolean') return true;
    if (LOGIC_NODE_TYPES.includes(candidate.type)) return true;
    return ['signal', 'position_trigger'].includes(candidate.type)
      && BOOLEAN_TRIGGER_ACTIONS.has(candidate.data?.action);
  });
};

// Tree picker partition of the panel's complete Linked Source list. A data source is represented
// only by its fields in Price; every other eligible graph source appears in In this strategy.
export const eligiblePriceOperandSources = (nodes = [], targetNode) => (
  eligibleOperandSources(nodes, targetNode).filter(({ type }) => type === 'data_source')
);

export const eligibleExistingOperandSources = (nodes = [], targetNode) => (
  eligibleOperandSources(nodes, targetNode).filter(({ type }) => type !== 'data_source')
);

const numericTargetIndex = (handle) => {
  if (handle === undefined || handle === null || handle === '') return null;
  const match = /^(?:input_)?(\d+)$/.exec(String(handle));
  return match ? Number(match[1]) : null;
};

const renderedTargetInputs = (targetNode) => {
  if (['signal', 'position_trigger'].includes(targetNode?.type)) {
    const configured = targetNode?.data?.inputs;
    return Array.isArray(configured) && configured.length > 0 ? configured : [{ id: 'input_0' }];
  }
  if (Array.isArray(targetNode?.data?.inputs)) return targetNode.data.inputs;
  if (targetInputKind(targetNode)) {
    return defaultLogicInputs(targetNode?.data?.operator);
  }
  return [];
};

export const targetInputSlots = (targetNode) => renderedTargetInputs(targetNode);

export const targetRequiresExistingInputSlot = (targetNode) => INPUT_SLOT_TARGET_TYPES.has(targetNode?.type);

const hasTargetSlotAuthority = (targetNode) => (
  targetRequiresExistingInputSlot(targetNode) || Array.isArray(targetNode?.data?.inputs)
);

const rendersSpecialTargetHandle = (targetNode, requestedHandle) => {
  if (requestedHandle !== 'trigger_input' || !['signal', 'position_trigger'].includes(targetNode?.type)) return false;
  const action = targetNode?.data?.action;
  const isStopOrTarget = action === 'sl' || action === 'tp';
  const isExit = isStopOrTarget || ['time_exit', 'exit_long', 'exit_short'].includes(action);
  return isExit && (!isStopOrTarget || targetNode?.data?.params?.exit_mode === 'CUSTOM');
};

export const resolveTargetHandle = (targetNode, requestedHandle, requestedIndex = null) => {
  const inputs = renderedTargetInputs(targetNode);
  if (requestedHandle === 'trigger_input') {
    return rendersSpecialTargetHandle(targetNode, requestedHandle) ? requestedHandle : null;
  }
  if (requestedHandle !== undefined && requestedHandle !== null && requestedHandle !== '') {
    const exact = inputs.find((input) => String(input?.id) === String(requestedHandle));
    if (exact?.id !== undefined && exact?.id !== null) return exact.id;
  }
  const numericIndex = numericTargetIndex(requestedHandle);
  const slotIndex = numericIndex ?? (Number.isInteger(requestedIndex) ? requestedIndex : null);
  if (slotIndex !== null && inputs[slotIndex]) {
    if (inputs[slotIndex].id !== undefined && inputs[slotIndex].id !== null) return inputs[slotIndex].id;
    if (targetInputKind(targetNode)) {
      return defaultLogicInputs(targetNode?.data?.operator)[slotIndex]?.id || null;
    }
  }
  if (requestedHandle !== undefined && requestedHandle !== null && requestedHandle !== '') {
    return hasTargetSlotAuthority(targetNode) ? null : String(requestedHandle);
  }
  return null;
};

// The one Tree attach-slot authority. It preserves saved slot ids, grows AND/OR groups through the same
// dynamic-input contract as LogicNode, and reuses an occupied signal handle so GraphWiring can append it.
export const resolveConditionAttachHandle = (targetNode, edges = []) => {
  const inputs = renderedTargetInputs(targetNode);
  const occupied = new Set((edges || [])
    .filter((edge) => edge.target === targetNode?.id)
    .map((edge) => resolveTargetHandle(
      targetNode,
      edge.targetHandle,
      edge.targetHandle === undefined || edge.targetHandle === null || edge.targetHandle === '' ? 0 : null,
    )));
  const freeIndex = inputs.findIndex((input, index) => {
    const handle = resolveTargetHandle(targetNode, input?.id, index);
    return handle !== null && handle !== undefined && handle !== '' && !occupied.has(handle);
  });
  if (freeIndex >= 0) return resolveTargetHandle(targetNode, inputs[freeIndex]?.id, freeIndex);
  if (isExpandableLogicTarget(targetNode)) return nextInputSlotId(inputs);
  if (['signal', 'position_trigger'].includes(targetNode?.type)) {
    return resolveTargetHandle(targetNode, inputs[0]?.id, 0);
  }
  return null;
};

// Materialize only the next valid dynamic AND/OR slot. Canvas connections to arbitrary unknown handles remain refused.
export const materializeConditionTargetSlot = (targetNode, requestedHandle) => {
  if (!isExpandableLogicTarget(targetNode)) return targetNode;
  if (resolveTargetHandle(targetNode, requestedHandle) !== null) return targetNode;
  const inputs = renderedTargetInputs(targetNode);
  const nextHandle = nextInputSlotId(inputs);
  if (String(requestedHandle) !== String(nextHandle)) return targetNode;
  return {
    ...targetNode,
    data: {
      ...targetNode.data,
      inputs: inputs.concat({ id: nextHandle, type: 'link', offset: 0 }),
    },
  };
};

export const resolveSourceFields = (sourceNode, availableFields, allMetadata) => {
  if (!sourceNode) return [];

  let fields = [];
  if (sourceNode.type === 'data_source') {
    const standard = availableFields?.standard || ['OPEN', 'HIGH', 'LOW', 'CLOSE', 'VOLUME'];
    const calculated = availableFields?.calculated || ['HL2', 'HLC3', 'OHLC4'];
    fields = Array.from(new Set([...standard, ...calculated].map((field) => field.toLowerCase())));
  } else {
    const nodeFields = sourceNode.data?.output_fields || sourceNode.data?.outputs || [];
    if (nodeFields.length > 0 && !(nodeFields.length === 1 && nodeFields[0]?.toLowerCase() === 'value')) {
      fields = nodeFields;
    } else if (sourceNode.type === 'indicator') {
      const normalize = (value) => (value || '').toLowerCase().replace(/[\s_]/g, '');
      const indicatorKeys = [sourceNode.data?.indicator, sourceNode.data?.name, sourceNode.data?.label]
        .filter(Boolean)
        .map(normalize);
      const metadata = allMetadata?.find((entry) => (
        indicatorKeys.includes(normalize(entry.name)) || indicatorKeys.includes(normalize(entry.display_name))
      ));

      if (metadata) {
        const dynamicFields = metadata.output_fields || metadata.outputs || [];
        if (dynamicFields.length > 0) fields = dynamicFields;
      }

      if (fields.length === 0) {
        const key = normalize(sourceNode.data?.indicator || '');
        if (key === 'bbands') fields = ['upper', 'middle', 'lower'];
        else if (key === 'stoch') fields = ['k', 'd'];
        else if (key === 'macd') fields = ['macd', 'signal', 'hist'];
        else if (key === 'supertrend') fields = ['value', 'direction'];
        else if (key === 'aroon') fields = ['aroon_up', 'aroon_down'];
        else if (key === 'adx') fields = ['adx', 'di_plus', 'di_minus'];
        else if (key === 'deetrendslope') fields = ['trend', 'slope', 'angle', 'strength', 'distance', 'ma', 'marker'];
        else if (key === 'ha' || key === 'heikinashi') fields = ['ha_open', 'ha_high', 'ha_low', 'ha_close'];
        else fields = ['value'];
      }
    } else {
      fields = ['value'];
    }
  }
  return fields;
};

export const matchesTargetInput = (edge, targetId, handleId, inputIndex = null) => {
  if (!edge || edge.target !== targetId) return false;
  const hasEdgeHandle = edge.targetHandle !== undefined && edge.targetHandle !== null && edge.targetHandle !== '';
  const hasRequestedHandle = handleId !== undefined && handleId !== null && handleId !== '';
  if (hasEdgeHandle && hasRequestedHandle && String(edge.targetHandle) === String(handleId)) return true;
  const requestedIndex = numericTargetIndex(handleId)
    ?? (Number.isInteger(inputIndex) ? inputIndex : null);
  if (requestedIndex === null) return false;
  if (!hasEdgeHandle) {
    return requestedIndex === 0;
  }
  return numericTargetIndex(edge.targetHandle) === requestedIndex;
};

export const canonicalSourceHandle = (sourceHandle) => (
  sourceHandle === undefined || sourceHandle === null || sourceHandle === '' ? 'value' : sourceHandle
);

const inputIndexFor = (inputs, targetHandle, inputIndex) => {
  const exactIndex = inputs.findIndex((input) => String(input?.id) === String(targetHandle));
  if (exactIndex >= 0) return exactIndex;
  const numericIndex = numericTargetIndex(targetHandle);
  if (numericIndex !== null && inputs[numericIndex]) return numericIndex;
  if (Number.isInteger(inputIndex) && inputIndex >= 0 && inputs[inputIndex]) return inputIndex;
  return -1;
};

const updateOneInput = (inputs, targetHandle, inputIndex, patch) => {
  const currentInputs = Array.isArray(inputs) ? inputs : [];
  const resolvedIndex = inputIndexFor(currentInputs, targetHandle, inputIndex);
  if (resolvedIndex >= 0 && resolvedIndex < currentInputs.length) {
    return currentInputs.map((input, index) => (index === resolvedIndex ? { ...input, ...patch } : input));
  }
  if (targetHandle === undefined || targetHandle === null || targetHandle === '') return currentInputs;
  return currentInputs.concat({
    id: targetHandle,
    offset: 0,
    ...patch,
  });
};

export const linkOperandInput = ({
  inputs = [],
  edges = [],
  targetNode,
  targetId,
  targetHandle = null,
  inputIndex = null,
  sourceId,
  sourceHandle,
  field,
  persistInput = true,
  edgeId,
  edgeStyle,
  edgeProps = {},
  replaceTargetInput = true,
}) => {
  const resolutionNode = targetNode || { data: { inputs } };
  const resolvedTarget = resolveTargetHandle(resolutionNode, targetHandle, inputIndex);
  const canonicalHandle = canonicalSourceHandle(sourceHandle ?? field);
  if (hasTargetSlotAuthority(resolutionNode) && resolvedTarget === null) {
    return { inputs, edges, sourceHandle: canonicalHandle };
  }
  const nextInputs = persistInput
    ? updateOneInput(inputs, resolvedTarget, inputIndex, {
      type: 'link',
      nodeId: sourceId || null,
      field: canonicalHandle,
    })
    : inputs;
  const nextEdges = replaceTargetInput
    ? edges.filter((edge) => !matchesTargetInput(edge, targetId, resolvedTarget, inputIndex))
    : edges;

  if (!sourceId) return { inputs: nextInputs, edges: nextEdges, sourceHandle: canonicalHandle };

  return {
    inputs: nextInputs,
    edges: nextEdges.concat({
      id: edgeId || `e_${sourceId}_${targetId}_${resolvedTarget}`,
      source: sourceId,
      target: targetId,
      targetHandle: resolvedTarget,
      sourceHandle: canonicalHandle,
      animated: true,
      ...edgeProps,
      ...(edgeStyle ? { style: edgeStyle } : {}),
    }),
    sourceHandle: canonicalHandle,
  };
};

export const setOperandField = ({
  inputs = [],
  edges = [],
  targetNode,
  targetId,
  targetHandle = null,
  inputIndex = null,
  field,
  persistInput = true,
}) => {
  const resolutionNode = targetNode || { data: { inputs } };
  const resolvedTarget = resolveTargetHandle(resolutionNode, targetHandle, inputIndex);
  if (hasTargetSlotAuthority(resolutionNode) && resolvedTarget === null) return { inputs, edges };
  return {
    inputs: persistInput ? updateOneInput(inputs, resolvedTarget, inputIndex, { field }) : inputs,
    edges: edges.map((edge) => (
      matchesTargetInput(edge, targetId, resolvedTarget, inputIndex) ? { ...edge, sourceHandle: field } : edge
    )),
  };
};

export const setOperandConstant = ({
  inputs = [],
  edges = [],
  targetNode,
  targetId,
  targetHandle = null,
  inputIndex = null,
  value,
}) => {
  const patch = { type: 'constant', nodeId: null, field: null };
  if (value !== undefined) patch.value = value;
  const resolutionNode = targetNode || { data: { inputs } };
  const resolvedTarget = resolveTargetHandle(resolutionNode, targetHandle, inputIndex);
  if (hasTargetSlotAuthority(resolutionNode) && resolvedTarget === null) return { inputs, edges };
  return {
    inputs: updateOneInput(inputs, resolvedTarget, inputIndex, patch),
    edges: edges.filter((edge) => !matchesTargetInput(edge, targetId, resolvedTarget, inputIndex)),
  };
};
